<script setup name="TvbHomeSmallBannerTemplateFour">
import { ref, inject, computed } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { FreeMode } from 'swiper/modules'
import { action } from './../../../../../../utils/gtm'
import { checkVideo } from '@/utils'

const swiperRef = ref()
const size = inject('size')

const props = defineProps({
  info: {
    type: Object,
    default: null
  },
  list: {
    type: Array,
    default: () => []
  }
})

const isMobile = computed(() => ['s', 'xs'].includes(size.value))
const offsetMap = {
  s: 16,
  xs: 12
}
const slidePrev = () => swiperRef?.value?.$el?.swiper?.slidePrev()
const slideNext = () => swiperRef?.value?.$el?.swiper?.slideNext()
const tracking = (item) => {
  action({
    page_title: props.info.title,
    screen_class: item.title || item.name,
    screenID: 'S001',
    eventCategory: 'home_banner',
    eventLabel: props.info.api,
    eventID: item.id || item.post_id,
    screenLabel: item.jump_url || item.link
  })
}

defineExpose({ slidePrev, slideNext })
</script>

<template>
  <main class="tvb-home-small-banner-video-main" :class="[size]">
    <swiper
      v-if="list.length"
      :key="size"
      :speed="800"
      class="tvb-home-small-banner-swiper"
      :slides-per-view="isMobile ? 1.1333 : 4"
      :slides-per-group="isMobile ? 1 : 4"
      :space-between="isMobile ? 10 : 16"
      :slides-offset-after="offsetMap[size]"
      :slides-offset-before="offsetMap[size]"
      :loop="!isMobile && 4 < list.length"
      :modules="[FreeMode]"
      :free-mode="{
        enabled: isMobile,
        momentum: false,
        minimumVelocity: 10,
        momentumBounceRatio: 0.01,
        momentumRatio: 0.01,
        momentumVelocityRatio: 0.01
      }"
      ref="swiperRef"
    >
      <swiper-slide
        class="tvb-home-small-banner-swiper-slide"
        v-for="(item, index) in list"
        :key="index"
      >
        <tvb-router-link
          :to="item.jump_url"
          :link-rel="item.link_rel"
          :link-target="item.link_target"
          class="tvb-home-small-banner-video-card"
          @click="tracking(item)"
        >
          <tvb-image
            class="tvb-home-small-banner-video-card-image"
            :border-radius="16"
            :play-icon="{ size: { xl: 48, l: 48, m: 48, s: 40, xs: 40 } }"
            :src="item.image"
            :icon="item.icon"
            :icon-text="item.icon_text"
            :alt="item.image_alt_text || item.title"
            :is-video="checkVideo(item)"
            transform
          />
          <div class="tvb-home-small-banner-video-card-titles">
            <h3 class="tvb-home-small-banner-video-card-title" :title="item.title">
              <span>{{ item.title }}</span>
            </h3>
          </div>
          <small class="tvb-home-small-banner-video-card-description" :title="item.description">
            {{ item.description }}
          </small>
        </tvb-router-link>
      </swiper-slide>
    </swiper>
  </main>
</template>

<style lang="scss" scoped>
.tvb-home-small-banner-video-main {
  margin-top: 16px;
  padding-bottom: 1px;

  .tvb-home-small-banner-swiper-slide {
    border-radius: 16px;
    border: 0.5px solid rgba(163, 178, 188, 0.35);
    background: #fcfcfc;
    padding-bottom: 12px;
  }

  .tvb-home-small-banner-video-card {
    text-decoration: none;
    position: relative;

    .tvb-home-small-banner-video-card-icon {
      height: 36px;
      width: 36px;
      position: absolute;
      top: 0;
      right: 0;
      z-index: 9;
      object-fit: cover;
      border-radius: 0 16px 0 0;
      overflow: hidden;
      @include safari-overflow-hidden();
    }

    .tvb-home-small-banner-video-card-image {
      width: 100%;
      aspect-ratio: 428/241;
      border-radius: 16px;
      overflow: hidden;
      --tvb-hairline-border-radius: calc(2 * 16px);
      @include safari-overflow-hidden();
    }

    .tvb-home-small-banner-video-card-titles {
      .tvb-home-small-banner-video-card-title {
        font-size: 18px;
        font-weight: 500;
        color: #231f20;
        line-height: 26px;
        word-break: break-all;
        letter-spacing: 0.5px;
        @include line-text-ellipsis(1);
        padding: 0 16px;
        span {
          @include text-hover-before();
          &:hover {
            @include text-hover-active();
          }
        }
      }
    }

    .tvb-home-small-banner-video-card-title {
      margin-top: 10px;
      // &:nth-last-child(-n + 2) {
      //   @include line-text-ellipsis(2);
      // }
    }
    .tvb-home-small-banner-video-card-description {
      margin-top: 2px;
      font-size: 16px;
      font-weight: 400;
      color: #444;
      line-height: 24px;
      letter-spacing: 0.5px;
      height: 48px;
      @include line-text-ellipsis(2);
      padding: 0 16px;
    }
  }

  &.s,
  &.xs {
    margin-top: 10px;
    .tvb-home-small-banner-video-card {
      .tvb-home-small-banner-video-card-icon {
        max-width: 24px;
        max-height: 24px;
        border-radius: 0 16px 0 0;
      }

      .tvb-home-small-banner-video-card-image {
        border-radius: 16px;
        aspect-ratio: 330/185.82;
        @supports not (aspect-ratio: 330/185.82) {
          height: 206px;
        }
      }

      .tvb-home-small-banner-video-card-title {
        margin-top: 8px;
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
      }

      .tvb-home-small-banner-video-card-description {
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: 0.3px;
        max-height: 40px;
        color: #555;
      }
    }
  }
}
</style>
